import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ArrowRight, Leaf } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  ["family", "A family member"],
  ["client", "The client themselves"],
  ["poa", "Power of Attorney / legal representative"],
  ["staff", "A Silver Home Care office staff member"],
  ["other", "A friend or someone else"],
];

export default function IntakeStartPage() {
  const [stage, setStage] = useState("welcome");
  const [role, setRole] = useState("");
  const [completerName, setCompleterName] = useState("");
  const [clientName, setClientName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [token, setToken] = useState("");
  const [roster, setRoster] = useState([]);
  const [otherStaff, setOtherStaff] = useState(false);
  const navigate = useNavigate();
  const [isDemo, setIsDemo] = useState(() => new URLSearchParams(window.location.search).has("demo"));

  const staffMode = role === "staff";

  useEffect(() => {
    if (staffMode && stage === "name" && roster.length === 0) {
      axios.get(`${BASE}/api/intake/staff-roster`)
        .then((r) => setRoster(r.data.names || []))
        .catch(() => setRoster([]));
    }
  }, [staffMode, stage, roster.length]);

  const createRecord = async (override) => {
    setBusy(true);
    setError("");
    try {
      const payload = override || {
        client_name: clientName.trim(),
        completer_name: completerName.trim(),
        completer_role: (ROLES.find(([k]) => k === role) || [])[1] || role,
      };
      const { data } = await axios.post(`${BASE}/api/intake/start`, payload);
      setToken(data.question_token || data.token);
      if (override) {
        navigate(`/intake-q/${data.question_token || data.token}?demo=1`);
      } else {
        setStage("instructions");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Something went wrong starting the intake — please call the office and we'll help you.");
    } finally {
      setBusy(false);
    }
  };

  const startDemo = () => {
    setIsDemo(true);
    createRecord({ client_name: "Demo Client", completer_name: "Demo User", completer_role: "A family member" });
  };

  const beginWizard = () => navigate(`/intake-q/${token}${isDemo ? "?demo=1" : ""}`);

  return (
    <div className="min-h-screen bg-accent/40 px-4 py-12" data-testid="intake-start-page">
      <div className="mx-auto max-w-xl">
        <div className="mb-6 flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary text-primary-foreground"><Leaf size={18} weight="fill" /></span>
          <span className="font-heading text-lg font-semibold tracking-tight text-stone-900">Silver Home Care</span>
        </div>

        {stage === "welcome" && (
          <div className="rounded-xl border border-stone-200 bg-white p-8 shadow-sm sm:p-10">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Silver Home Care</p>
            <h1 className="mt-2 font-heading text-3xl font-semibold text-stone-900">New Client Intake</h1>
            <div className="mt-4 space-y-3 text-base leading-relaxed text-stone-600">
              <p>Welcome — this link starts a brand-new intake. You don't need an account or a password, and you don't need to know anything before you begin.</p>
              <p>First we'll ask who's completing this and who it's for. Then we'll ask the questions <strong>one at a time</strong> — about needs, the home, health, and medications. Most people finish in about 10 minutes, and everything saves as you go.</p>
              <p>At the end, the documents will be ready to review and sign — that's the only signing you'll need to do.</p>
            </div>
            <button type="button" onClick={() => setStage("role")}
              className="mt-7 inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-all hover:bg-stone-900"
              data-testid="intake-start-button">
              Get started <ArrowRight size={17} />
            </button>
            {!isDemo && (
              <button type="button" onClick={startDemo} disabled={busy}
                className="mt-3 inline-flex items-center gap-2 rounded-lg border border-[#B7791F]/40 bg-[#B7791F]/5 px-6 py-3 text-sm font-semibold text-[#8a5d14] transition-colors hover:bg-[#B7791F]/15 disabled:opacity-50"
                data-testid="intake-start-demo-button">
                {busy ? "Starting..." : "Try it with demo answers — straight into the questions"}
              </button>
            )}
          </div>
        )}

        {stage === "role" && (
          <div className="rounded-xl border border-stone-200 bg-white p-8 shadow-sm sm:p-10" data-testid="intake-start-role-step">
            <h2 className="font-heading text-2xl font-semibold text-stone-900">First — who is completing this intake?</h2>
            <p className="mt-1 text-sm text-stone-500">Pick the one that fits best. We'll word the questions accordingly.</p>
            <div className="mt-5 space-y-2.5">
              {ROLES.map(([key, label]) => (
                <button key={key} type="button"
                  onClick={() => { setRole(key); setStage("name"); }}
                  className={`flex w-full items-center justify-between rounded-lg border px-4 py-3.5 text-left text-base font-medium transition-colors ${role === key ? "border-primary bg-primary/10 text-primary" : "border-stone-200 bg-white text-stone-700 hover:border-primary/50 hover:bg-primary/5"}`}
                  data-testid={`intake-start-role-${key}`}>
                  {label} <ArrowRight size={16} className="text-stone-400" />
                </button>
              ))}
            </div>
            <button type="button" onClick={() => setStage("welcome")} className="mt-5 inline-flex items-center gap-1 text-sm font-medium text-stone-400 hover:text-stone-600" data-testid="intake-start-role-back">
              <ArrowLeft size={14} /> Back
            </button>
          </div>
        )}

        {stage === "name" && (
          <div className="rounded-xl border border-stone-200 bg-white p-8 shadow-sm sm:p-10" data-testid="intake-start-name-step">
            <h2 className="font-heading text-2xl font-semibold text-stone-900">{staffMode ? "Which of the office team is running this intake?" : "And your name?"}</h2>
            <p className="mt-1 text-sm text-stone-500">{staffMode ? "It's recorded on the intake so the record shows who collected the answers." : "So we know who we spoke with, and can address the questions to you."}</p>
            {staffMode && !otherStaff ? (
              <select value={completerName}
                onChange={(e) => { if (e.target.value === "__other") { setOtherStaff(true); setCompleterName(""); } else setCompleterName(e.target.value); }}
                autoFocus
                className="mt-5 w-full rounded-lg border border-stone-300 bg-white px-4 py-3 text-lg focus:border-primary focus:outline-none"
                data-testid="intake-start-completer-select">
                <option value="">Choose your name…</option>
                {roster.map((n) => <option key={n} value={n} data-testid={`intake-start-staff-${n.toLowerCase()}`}>{n}</option>)}
                <option value="__other">Someone else / not listed</option>
              </select>
            ) : (
              <input type="text" value={completerName} onChange={(e) => setCompleterName(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && completerName.trim()) setStage("client"); }}
                autoFocus placeholder="Your full name"
                className="mt-5 w-full rounded-lg border border-stone-300 bg-white px-4 py-3 text-lg focus:border-primary focus:outline-none"
                data-testid="intake-start-completer-name" />
            )}
            <div className="mt-6 flex items-center justify-between">
              <button type="button" onClick={() => setStage("role")} className="inline-flex items-center gap-1 text-sm font-medium text-stone-400 hover:text-stone-600" data-testid="intake-start-name-back">
                <ArrowLeft size={14} /> Back
              </button>
              <button type="button" onClick={() => setStage("client")} disabled={!completerName.trim()}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white shadow-sm transition-all hover:bg-stone-900 disabled:opacity-50"
                data-testid="intake-start-name-continue">
                Continue <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {stage === "client" && (
          <div className="rounded-xl border border-stone-200 bg-white p-8 shadow-sm sm:p-10" data-testid="intake-start-client-step">
            <h2 className="font-heading text-2xl font-semibold text-stone-900">Who is this intake for?</h2>
            <p className="mt-1 text-sm text-stone-500">The person who will receive care. This creates their record — everything you answer next gets filed to it.</p>
            <input type="text" value={clientName} onChange={(e) => setClientName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter" && clientName.trim() && !busy) createRecord(); }}
              autoFocus placeholder="Client's full name"
              className="mt-5 w-full rounded-lg border border-stone-300 bg-white px-4 py-3 text-lg focus:border-primary focus:outline-none"
              data-testid="intake-start-client-name" />
            {error && <p className="mt-4 text-sm font-medium text-[#C0392B]" data-testid="intake-start-error">{error}</p>}
            <div className="mt-6 flex items-center justify-between">
              <button type="button" onClick={() => setStage("name")} className="inline-flex items-center gap-1 text-sm font-medium text-stone-400 hover:text-stone-600" data-testid="intake-start-client-back">
                <ArrowLeft size={14} /> Back
              </button>
              <button type="button" onClick={() => createRecord()} disabled={!clientName.trim() || busy}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 py-3 text-base font-semibold text-white shadow-sm transition-all hover:bg-stone-900 disabled:opacity-50"
                data-testid="intake-start-create-button">
                {busy ? "Creating record..." : "Start the intake"} <ArrowRight size={16} />
              </button>
            </div>
          </div>
        )}

        {stage === "instructions" && (
          <div className="rounded-xl border border-stone-200 bg-white p-8 shadow-sm sm:p-10" data-testid="intake-start-instructions">
            <p className="text-[11px] font-semibold uppercase tracking-wider text-stone-400">Record started for {clientName.trim()}</p>
            <h2 className="mt-2 font-heading text-2xl font-semibold text-stone-900">
              {staffMode ? "You're ready to run the intake." : `Thank you, ${completerName.trim().split(" ")[0]} — here's how this works.`}
            </h2>
            {staffMode ? (
              <div className="mt-4 space-y-3 text-base leading-relaxed text-stone-600">
                <p><strong>Read each question aloud, exactly as written,</strong> and tap the answer they give you. You're their voice in this — record what they say, not what you assume.</p>
                <p>If they don't know something, use <strong>Skip</strong> — never guess. The office reviews everything afterward.</p>
                <p>When the questions are done, the signing step appears: <strong>sign right on this device</strong>, or <strong>email the signing link</strong> to the client or family.</p>
              </div>
            ) : role === "client" ? (
              <div className="mt-4 space-y-3 text-base leading-relaxed text-stone-600">
                <p>We'll ask the questions <strong>one at a time</strong> — they're about you, your home, and your health. Answer what you know and skip what you don't.</p>
                <p>Everything saves as you go, so you can stop and come back to this link anytime before it expires.</p>
                <p>When the questions are done, your documents will be ready to review and sign — that's the only signing you'll need to do.</p>
              </div>
            ) : (
              <div className="mt-4 space-y-3 text-base leading-relaxed text-stone-600">
                <p>We'll ask the questions <strong>one at a time</strong> — no long forms. Answer what you know and skip what you don't.</p>
                <p>Everything saves as you go, so you can stop and come back to this link anytime before it expires.</p>
                <p>When the questions are done, the documents will be ready to review and sign — that's the only signing you'll need to do.</p>
              </div>
            )}
            {isDemo && (
              <p className="mt-4 rounded-lg border border-[#B7791F]/30 bg-[#B7791F]/5 px-4 py-3 text-sm text-[#8a5d14]" data-testid="intake-start-demo-note">
                Demo mode is on — inside, look for <strong>“Demo: fill everything”</strong> to fill all the questions with realistic test answers and click through to the end.
              </p>
            )}
            <button type="button" onClick={beginWizard}
              className="mt-7 inline-flex items-center gap-2 rounded-lg bg-primary px-7 py-3.5 text-base font-semibold text-white shadow-sm transition-all hover:bg-stone-900"
              data-testid="intake-start-begin-wizard">
              Begin the questions <ArrowRight size={17} />
            </button>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-stone-400">Your information is stored securely and only shared with the care team.</p>
      </div>
    </div>
  );
}
